import config from "config";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { isNativePlatform } from "src/capacitor/appsHelpers";
import "src/components/TawkMessenger/tawk.d";
import { useChatUrl } from "src/components/TawkMessenger/useChatUrl";
import isPes from "src/helpers/isPes";
import { useGetUserInfo } from "src/hooks/useUser";

interface TawkContextValue {
	openChat: (() => void) | null;
	chatUrl: string | null;
}

const TawkContext = createContext<TawkContextValue>({
	openChat: null,
	chatUrl: null,
});

export const useTawk = () => useContext(TawkContext);

interface TawkMessengerProviderProps {
	children: ReactNode;
}

export function TawkMessengerProvider({
	children,
}: TawkMessengerProviderProps) {
	const chatUrl = useChatUrl();

	if (isNativePlatform()) {
		return (
			<TawkContext.Provider value={{ openChat: null, chatUrl }}>
				{children}
			</TawkContext.Provider>
		);
	}

	return <TawkWebProvider>{children}</TawkWebProvider>;
}

function TawkWebProvider({ children }: { children: ReactNode }) {
	const { data: user } = useGetUserInfo();
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		if (!config.tawkTo) return;

		window.Tawk_API = window.Tawk_API || {};
		window.Tawk_LoadStart = new Date();

		window.Tawk_API.onBeforeLoad = () => {
			window.Tawk_API?.hideWidget?.();
		};
		window.Tawk_API.onLoad = () => {
			setIsReady(true);
		};
		window.Tawk_API.onChatMinimized = () => {
			window.Tawk_API?.hideWidget?.();
		};

		const script = document.createElement("script");
		script.async = true;
		script.src = `https://embed.tawk.to/${config.tawkTo.propertyId}/${config.tawkTo.widgetId}`;
		script.setAttribute("crossorigin", "*");
		document.head.appendChild(script);
	}, []);

	useEffect(() => {
		if (!isReady || !user) return;
		const api = window.Tawk_API;
		if (!api?.setAttributes) return;

		const baseAttrs: Record<string, string> = {
			userId: String(user.id),
			specId: String(user.selectedSpecialisation?.id ?? ""),
			specName: String(user.selectedSpecialisation?.specialisation ?? ""),
			login: user.username ?? "",
			name: user.username ?? "",
			service: config.dictionary?.serviceName ?? "",
			isPes: String(isPes()),
		};
		api.setAttributes({ ...baseAttrs, email: user.email ?? "" }, (err) => {
			if (err === "INVALID_EMAIL") {
				api.setAttributes?.(baseAttrs, console.error);
			} else if (err) {
				console.error(err);
			}
		});
	}, [isReady, user]);

	const openChat = () => {
		const api = window.Tawk_API;
		if (!api?.maximize) return;
		api.showWidget?.();
		api.maximize();
	};

	const value: TawkContextValue =
		!config.tawkTo || !user
			? { openChat: null, chatUrl: null }
			: { openChat, chatUrl: null };

	return <TawkContext.Provider value={value}>{children}</TawkContext.Provider>;
}
